import React, { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Toast } from 'primereact/toast'
import { Button } from 'primereact/button'
import { FileUpload } from 'primereact/fileupload'
import { Toolbar } from 'primereact/toolbar'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'
import { Slider } from 'primereact/slider'
import { TabView, TabPanel } from 'primereact/tabview'
import { Skeleton } from 'primereact/skeleton';
import * as R from 'ramda'
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { listAllCompaniesRequest, listAllEControllersRequest, createEControllerRequest, editEControllerRequest, unbindEControllerRequest } from '../apis/calyxSaaSDashboardAPI'
import axios from 'axios'
import { StatusCodes } from 'http-status-codes';
import { useStore } from '../stores/GlobalStore'


export const CrudEController = () => {
  const emptyEController = {
    fullSerialNo: '',
    companyId: '',
  }


  const queryClient = useQueryClient()
  const [companyDropdown, setCompanyDropdown] = useState(null)
  const [companyMap, setCompanyMap] = useState(null)
  const [createOrEditEControllerDialogVisibility, setCreateOrEditDeviceDialogVisibility] = useState(false)
  const [isCreateEControllerMode, setIsCreateEControllerMode] = useState(false)
  const [eControllerDetailDialog, setDeviceDetailDialog] = useState(false)
  const [focusedEController, setFocusedEController] = useState(emptyEController)
  const [globalFilter, setGlobalFilter] = useState(null)
  const toast = useRef(null)
  const dt = useRef(null)
  const { state, dispatch } = useStore()

  const allCompaniesQuery = useQuery(['list', 'companies'], () => {
    return listAllCompaniesRequest()
  }, {
    refetchOnWindowFocus: false,
    retry: 0,
    onSuccess: (companies) => {
      const companyMap = companies.reduce((acc, c) => ({ ...acc, [c.id]: c }), {})
      setCompanyMap(companyMap)
      setCompanyDropdown(Object.keys(companyMap).map(cid => ({ label: companyMap[cid].name, value: companyMap[cid].id })))
    },
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
          let countDownSecond = 3;
          toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
          setInterval(() => {
            countDownSecond--;
            if (countDownSecond <= 0) {
              dispatch({
                type: 'LOGOUT',
              })
            }
          }, 1000);
        } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
          toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
        } else {
          toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
        }
      } else {
        toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
      }
    }
  })

  const allEControllersQuery = useQuery(['list', 'e-controllers'], () => {
    return listAllEControllersRequest()
  }, {
    refetchOnWindowFocus: false,
    retry: 0,
    enabled: !!companyMap,
    onSuccess: (allEControllers) => {
      const allEControllersWithCompanyName = allEControllers.map(eController => {
        const dedicatedCompany = companyMap[eController.companyId]
        if (dedicatedCompany) {
          return {
            ...eController,
            companyName: dedicatedCompany.name
          }
        } else {
          return {
            ...eController,
            companyName: ''
          }
        }
      })
      queryClient.setQueryData(['list', 'e-controllers'], allEControllersWithCompanyName)
    },
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
          let countDownSecond = 3;
          toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
          setInterval(() => {
            countDownSecond--;
            if (countDownSecond <= 0) {
              dispatch({
                type: 'LOGOUT',
              })
            }
          }, 1000);
        } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
          toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
        } else {
          toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
        }
      } else {
        toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
      }
    }
  })

  const createEControllerMutation = useMutation(requestBody => {
    return createEControllerRequest(requestBody)
  },
    {
      onSuccess: (savedNewEController) => {
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'New e-controller has been saved', life: 3000 })
        queryClient.setQueryData(['list', 'e-controllers'], staleEControllers => {
          savedNewEController.companyName = companyMap[savedNewEController.companyId].name || ''
          const newEControllers = [savedNewEController, ...staleEControllers]
          return newEControllers
        })
        hideCreateOrEditEControllerDialog()
      },
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 3;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
            setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.BAD_REQUEST) {
            toast.current.show({ severity: 'error', summary: err.response?.data?.message, life: 5000 });
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
        }
      }
    })

  const unbindEControllerMutation = useMutation(unbindEcontrollerOption => {
    return unbindEControllerRequest(unbindEcontrollerOption.companyId, unbindEcontrollerOption.houseId, unbindEcontrollerOption.eControllerId)
  },
    {
      onSuccess: (updatedEController) => {
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Selected e-controller has been unbinded with house', life: 3000 })
        queryClient.setQueryData(['list', 'e-controllers'], staleEControllers => {
          const newEControllers = [...staleEControllers]
          const idx = newEControllers.findIndex(staleEController => staleEController.id === updatedEController.id)
          if (idx >= 0) {
            updatedEController.companyName = companyMap[updatedEController.companyId].name || ''
            newEControllers[idx] = updatedEController
            return newEControllers
          } else {
            toast.current.show({ severity: 'error', summary: 'Something went wrong, please try again later', life: 5000 });
          }
        })
      },
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 3;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
            setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.BAD_REQUEST) {
            toast.current.show({ severity: 'error', summary: err.response?.data?.message, life: 5000 });
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
        }
      }
    })

  const patchEControllerMutation = useMutation(patchEControllerOption => {
    return editEControllerRequest(patchEControllerOption.originalCompanyId, patchEControllerOption.eControllerId, patchEControllerOption.requestBody)
  },
    {
      onSuccess: (updatedEController) => {
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Selected e-controller has been updated', life: 10000 })
        toast.current.show({ severity: 'info', summary: 'Info', detail: 'e-controller may been unbind automatically with house, if you bind it to another company.', life: 10000 })
        queryClient.setQueryData(['list', 'e-controllers'], staleEControllers => {
          const newEControllers = [...staleEControllers]
          const idx = newEControllers.findIndex(staleDevice => staleDevice.id === updatedEController.id)
          if (idx >= 0) {
            updatedEController.companyName = companyMap[updatedEController.companyId].name || ''
            newEControllers[idx] = updatedEController
            return newEControllers
          } else {
            toast.current.show({ severity: 'error', summary: 'Something went wrong, please try again later', life: 5000 });
          }
        })
        hideCreateOrEditEControllerDialog()
      },
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 3;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
            setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.BAD_REQUEST) {
            toast.current.show({ severity: 'error', summary: err.response?.data?.message, life: 5000 });
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
        }
      }
    })

  const openCreateEControllerDialog = () => {
    setIsCreateEControllerMode(true)
    setFocusedEController(emptyEController)
    setCreateOrEditDeviceDialogVisibility(true)
  }

  const hideCreateOrEditEControllerDialog = () => {
    setIsCreateEControllerMode(false)
    setCreateOrEditDeviceDialogVisibility(false)
  }

  const hideEControllerDetailDialog = () => {
    setDeviceDetailDialog(false)
  }

  const createOrEditEController = () => {
    const isValidEController = validateEController(focusedEController)
    if (!isValidEController) {
      toast.current.show({ severity: 'error', summary: 'Please fill in required fields before apply.', life: 4000 });
      return
    }
    if (isCreateEControllerMode) {
      const requestBody = { ...focusedEController }
      createEControllerMutation.mutate(requestBody)
    } else {
      patchEControllerMutation.mutate({
        originalCompanyId: focusedEController.originalCompanyId,
        eControllerId: focusedEController.id,
        requestBody: {
          ...focusedEController
        }
      })
    }
  }

  const validateEController = (eController) => {
    if (eController?.companyId && eController?.fullSerialNo) {
      return true
    } else {
      return false
    }
  }

  const editEController = (device) => {
    setFocusedEController({ ...device, originalCompanyId: device.companyId })
    setCreateOrEditDeviceDialogVisibility(true)
  }

  const showEControllerDetail = (device) => {
    setFocusedEController(device)
    setDeviceDetailDialog(true)
  }

  const exportCSV = () => {
    dt.current.exportCSV()
  }

  const onInputChange = (e, name) => {
    const val = (e.target && e.target.value) || ''
    const _device = { ...focusedEController }
    _device[`${name}`] = val

    setFocusedEController(_device)
  }

  const unbindEController = (eController) => {
    const unbindEControllerOption = {
      companyId: eController.companyId,
      houseId: eController.houseId,
      eControllerId: eController.id
    }
    unbindEControllerMutation.mutate(unbindEControllerOption)
  }

  const leftToolbarTemplate = () => {
    return (
      <>
        <div className='my-2'>
          <Button label='New' icon='pi pi-plus' className='p-button-success mr-2' onClick={openCreateEControllerDialog} />
        </div>
      </>
    )
  }

  const rightToolbarTemplate = () => {
    return (
      <>
        <FileUpload mode='basic' accept='image/*' maxFileSize={1000000} label='Import' chooseLabel='Import' className='mr-2 inline-block' />
        <Button label='Export' icon='pi pi-upload' className='p-button-help' onClick={exportCSV} />
      </>
    )
  }

  const serialNumberBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Serial Number</span>
        {rowData.serialNumber}
      </>
    )
  }

  const remarkBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Remark</span>
        {rowData.remark}
      </>
    )
  }

  const companyBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Company</span>
        {rowData.companyName || ''}
      </>
    )
  }

  const serviceBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Service</span>
        {rowData.service}
      </>
    )
  }

  const fullSerialNoBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Full Serial Number</span>
        {rowData.fullSerialNo || ''}
      </>
    )
  }

  const houseIdBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Position</span>
        {rowData.houseId || ''}
      </>
    )
  }

  const actionBodyTemplate = (rowData) => {
    return (
      <div className='actions'>
        <Button tooltip='detail' icon='pi pi-info-circle' className='p-button-rounded p-button-info mt-2' onClick={() => showEControllerDetail(rowData)} />
        <Button tooltip='edit' icon='pi pi-pencil' className='p-button-rounded p-button-success mt-2' onClick={() => editEController(rowData)} />
      </div>
    )
  }

  const connectionBodyTemplate = (rowData) => {
    if (R.isNil(rowData.houseId)) {
      return (
        <div className='actions' />
      )
    } else {
      return (
        <div className='actions'>
          <Button tooltip='unbind e-controller with house' icon='pi pi-link' className='p-button-rounded p-button-danger mt-2' onClick={() => unbindEController(rowData)} />
        </div>
      )
    }
  }

  const header = (
    <div className='flex flex-column md:flex-row md:justify-content-between md:align-items-center'>
      <h5 className='m-0'>Manage E-Controllers</h5>
      <IconField iconPosition="left" className='block mt-2 md:mt-0'>
        <InputIcon className='pi pi-search' />
        <InputText type='search' onInput={(e) => setGlobalFilter(e.target.value)} placeholder='Search...' />
      </IconField>
    </div>
  )

  const eControllerDialogFooter = (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideCreateOrEditEControllerDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' onClick={createOrEditEController} />
    </>
  )

  return (
    <div className='grid crud-demo'>
      <div className='col-12'>
        <div className='card'>
          <Toast ref={toast} />
          <Toolbar className='mb-4' left={leftToolbarTemplate} right={rightToolbarTemplate} />

          {allCompaniesQuery.isFetching || allEControllersQuery.isFetching ?
            (
              <DataTable value={Array.from({ length: 10 })} className="p-datatable-striped company-table-skeleton" header={header}>
                <Column header='Full Serial Number' field='fullSerialNo' style={{ width: '20%', minWidth: '10rem' }} body={<Skeleton />}></Column>
                <Column header='Company' field='company' style={{ width: '20%', minWidth: '10rem' }} body={<Skeleton />}></Column>
                <Column header='House' field='houseId' style={{ width: '20%', minWidth: '10rem' }} body={<Skeleton />}></Column>
                <Column header='Unbind' style={{ width: '20%', minWidth: '10rem' }} body={<Skeleton />}></Column>
                <Column header='Action' style={{ width: '20%', minWidth: '10rem' }} body={<Skeleton />}></Column>
              </DataTable>
            )
            :
            (
              <DataTable
                ref={dt} value={allEControllersQuery.data}
                dataKey='id' paginator rows={50} rowsPerPageOptions={[5, 10, 25, 50, 100]} className='datatable-responsive'
                paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
                currentPageReportTemplate='Showing {first} to {last} of {totalRecords} devices'
                globalFilter={globalFilter}
                emptyMessage='No devices found.' header={header} responsiveLayout='scroll'
              >
                <Column header='Full Serial Number' field='fullSerialNo' sortable body={fullSerialNoBodyTemplate} headerStyle={{ width: '20%', minWidth: '10rem' }} />
                <Column header='Company' field='companyName' sortable body={companyBodyTemplate} headerStyle={{ width: '20%', minWidth: '10rem' }} />
                <Column header='House' field='houseId' body={houseIdBodyTemplate} headerStyle={{ width: '20%', minWidth: '10rem' }} />
                <Column header='Unbind' body={connectionBodyTemplate} headerStyle={{ width: '20%', minWidth: '10rem' }} />
                <Column header='Action' body={actionBodyTemplate} headerStyle={{ width: '20%', minWidth: '10rem' }} />
              </DataTable>
            )
          }

          <Dialog blockScroll={true} visible={createOrEditEControllerDialogVisibility} style={{ width: '450px' }} header={isCreateEControllerMode ? 'Create new e-controller' : 'Edit e-controller'} modal className='p-fluid' footer={eControllerDialogFooter} onHide={hideCreateOrEditEControllerDialog}>
            <div className='field'>
              <label htmlFor='fullSerialNo'>Serial No.</label>
              <InputText
                id='fullSerialNo'
                value={focusedEController.fullSerialNo}
                onChange={(e) => onInputChange(e, 'fullSerialNo')}
                required autoFocus
                disabled={createEControllerMutation.isLoading || patchEControllerMutation.isLoading}
              />
              {!focusedEController.fullSerialNo && <small className='p-invalid' style={{ color: 'red' }} >Full Serial Number is required.</small>}
            </div>

            <div className='field'>
              <label htmlFor='company'>Company</label>
              <Dropdown
                id='company' value={focusedEController.companyId} options={companyDropdown} filter onChange={(e) => onInputChange(e, 'companyId')}
                required
                disabled={createEControllerMutation.isLoading || patchEControllerMutation.isLoading}
              />
              {!focusedEController.companyId && <small className='p-invalid' style={{ color: 'red' }} >Compayn is required.</small>}
            </div>
          </Dialog>

          <Dialog blockScroll={true} visible={eControllerDetailDialog} style={{ width: '450px' }} header='E-Controller Detail' modal onHide={hideEControllerDetailDialog}>
            <div className='field'>
              <label htmlFor='fullSerialNo' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Serial No.: </label><label>{focusedEController.fullSerialNo}</label>
            </div>
            <div className='field'>
              <label htmlFor='company' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Company: </label><label>{focusedEController.companyId}</label>
            </div>
            <div className='field'>
              <label htmlFor='houseId' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >House Id: </label><label>{focusedEController.houseId || <code>empty</code>}</label>
            </div>
            <div className='field'>
              <label htmlFor='eControllerId' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >E-controller Id: </label><label>{focusedEController.id}</label>
            </div>
            <div className='field'>
              <label htmlFor='createdTime' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Created at: </label><label>{focusedEController.createdTime || <code>unknown</code> }</label>
            </div>
            <div className='field'>
              <label htmlFor='updatedTime' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Last updated at: </label><label>{focusedEController.updatedTime || <code>unknown</code> }</label>
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  )
}
